import { useEffect, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import * as Sentry from '@sentry/react'
import { useShallow } from 'zustand/react/shallow'

import { SetHighlightProps } from '../stores/app-store'
import useAppStore from '../stores/store'
import { getSelection } from '../utils/office'

const useSelectedText = (
  setHighlightedText: ({ highlightedText }: SetHighlightProps) => void
) => {
  const { resetServerState, isApplyingSuggestion, isFetching } = useAppStore(
    useShallow((s) => ({
      resetServerState: s.resetServerState,
      isApplyingSuggestion: s.isApplyingSuggestion,
      isFetching: s.isFetching,
    }))
  )

  const isApplyingSuggestionRef = useRef(isApplyingSuggestion)
  useEffect(() => {
    isApplyingSuggestionRef.current = isApplyingSuggestion
  }, [isApplyingSuggestion])

  useEffect(() => {
    const handleSelectionChange = async () => {
      if (isApplyingSuggestionRef.current || isFetching) return
      datadogRum.startDurationVital('wordAddInHandleSelectionChange')
      try {
        await getSelection({ callback: setHighlightedText })
      } catch (error) {
        console.error('Error fetching selected text:', error)
        Sentry.captureException(error)
      } finally {
        datadogRum.stopDurationVital('wordAddInHandleSelectionChange')
      }
    }
    const run = () => {
      // Add event listener for selection changes
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        handleSelectionChange
      )
    }
    run()
    // Cleanup event listener on unmount
    return () => {
      Office.context.document.removeHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        handleSelectionChange
      )
    }
  }, [setHighlightedText, resetServerState, isFetching])
}

export default useSelectedText
